import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { RestorationTypesEnum } from '@modules/tooth-editor/models/restoration-types.enum';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { RestorationTypeFacade, RestorationTypeIds } from '@modules/tooth-editor/restoration-type.facade';
import { RestorationTypeForm } from '@modules/tooth-editor/models/restoration-type-from';
import { Tooth } from '@modules/teeth-diagram/models/tooth';

@Component({
	selector: 'rx-restoration-type-section',
	styleUrls: ['./restoration-type-section.component.scss'],
	templateUrl: 'restoration-type-section.component.html',
	providers: [RestorationTypeFacade],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class RestorationTypeSectionComponent extends BaseDestroyableComponent implements OnInit, OnChanges {
	private restorationDataIds = new BehaviorSubject<RestorationTypeIds>({
		ImplantBasedRestorationTypeId: null,
		AbutmentType: null,
		AbutmentMaterialId: null
	});

	/* eslint-disable @typescript-eslint/naming-convention */
	@Input() ImplantBasedRestorationTypeId: Tooth['ImplantBasedRestorationTypeId'];
	@Input() AbutmentType: Tooth['AbutmentType'];
	@Input() AbutmentMaterialId: Tooth['AbutmentMaterialId'];
	/* eslint-enable @typescript-eslint/naming-convention */
	@Input() isReadOnly = false;

	@Output() restorationTypeChanged = new EventEmitter<RestorationTypeIds>();

	form = this.formBuilder.group<RestorationTypeForm>({
		restorationType: null,
		abutmentType: null,
		abutmentMaterial: null,
		isTiBased: false
	});

	get restorationTypeControl() {
		return this.form.controls.restorationType;
	}

	get abutmentTypeControl() {
		return this.form.controls.abutmentType;
	}

	get abutmentMaterialControl() {
		return this.form.controls.abutmentMaterial;
	}

	get isTiBasedControl() {
		return this.form.controls.isTiBased;
	}

	restorationTypeId$ = this.restorationDataIds.pipe(map(ids => ids.ImplantBasedRestorationTypeId));

	abutmentTypeId$ = this.restorationDataIds.pipe(map(ids => ids.AbutmentType));

	restorationTypes$ = this.facade.restorationTypes$;
	abutmentTypes$ = this.facade.abutmentTypes$;

	materials$ = this.facade.getMaterials$(this.abutmentTypeId$).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	isScrewRetainedOrEmpty$ = this.restorationTypeId$.pipe(
		map(restorationTypeId => restorationTypeId === RestorationTypesEnum.ScrewRetained || !restorationTypeId),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	isTiBasedDisabled$ = combineLatest([this.isScrewRetainedOrEmpty$, this.abutmentTypeId$]).pipe(
		map(([isScrewRetainedOrEmpty, abutmentTypeId]) => isScrewRetainedOrEmpty || !abutmentTypeId || this.isReadOnly),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	constructor(
		private formBuilder: FormBuilder,
		private facade: RestorationTypeFacade
	) {
		super();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.ImplantBasedRestorationTypeId || changes.AbutmentType || changes.AbutmentMaterialId) {
			this.restorationDataIds.next({
				ImplantBasedRestorationTypeId: this.ImplantBasedRestorationTypeId,
				AbutmentType: this.AbutmentType,
				AbutmentMaterialId: this.AbutmentMaterialId
			});
		}
	}

	ngOnInit(): void {
		merge(this.subscribeToUserChanges(), this.subscribeToSelectedToothChanges()).pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	cleanControlWhenDisabled(isDisabled: boolean, formControl: FormControl): void {
		if (isDisabled && !this.isReadOnly) {
			formControl.patchValue(null);
		}
	}

	switchOffIsTiBasedControl(isDisabled: boolean): void {
		if (isDisabled && !this.isReadOnly) {
			this.isTiBasedControl?.patchValue(false);
		}
	}

	private subscribeToUserChanges() {
		return this.form.valueChanges.pipe(
			distinctUntilChanged(),
			tap((formValue: RestorationTypeForm) => {
				const previousIds = this.restorationDataIds.value;
				const updatedIds = this.facade.buildUpdatedIds(formValue, previousIds);

				this.restorationDataIds.next(updatedIds);

				if (updatedIds.AbutmentMaterialId !== previousIds.AbutmentMaterialId) {
					this.abutmentMaterialControl.patchValue(null, { emitEvent: false });
				}

				this.restorationTypeChanged.emit(updatedIds);
			})
		);
	}

	private subscribeToSelectedToothChanges(): Observable<RestorationTypeForm> {
		return this.facade.getRestorationTypeForm$(this.restorationDataIds).pipe(
			filter(formValues => !!formValues),
			tap(formValues => this.form.patchValue(formValues, { emitEvent: false }))
		);
	}
}
